import { ref, Ref } from 'vue'

const timeoutRecall = 5000 // 5 segundos: backoff base ante fallo al obtener el token
const maxRecall = 60000 // tope del backoff exponencial (1 min)

// Manejador de tokens
// Se encarga de gestionar la obtención y renovación de tokens de acceso para la autenticación de usuarios.
class TokenManager {
  private access_token: string | null = null
  private lastFetched: number | null = null
  private tokenExpiry: number = 0
  private updateCaller: NodeJS.Timeout | null = null
  private tokenRef: Ref<string | null> = ref(null)
  private subscribers: Set<(data: string | null) => void> = new Set()
  private getPromise: Promise<string> | null = null
  private retryDelay: number = timeoutRecall

  constructor() {}

  startup() {
    this.getToken()
  }

  /**
   * Obtiene el token de acceso. Deduplica peticiones concurrentes: mientras hay
   * una en curso, todos los callers comparten la misma promesa.
   * Nunca rechaza: ante fallo resuelve con el último token conocido (o cadena
   * vacía) y reprograma un reintento con backoff, evitando el bucle de
   * peticiones instantáneas.
   */
  async getToken(): Promise<string> {
    if (this.getPromise) {
      return this.getPromise
    }

    this.getPromise = this.fetchToken()
    try {
      return await this.getPromise
    } finally {
      this.getPromise = null
    }
  }

  private async fetchToken(): Promise<string> {
    try {
      const params = new URLSearchParams()
      params.append('grant_type', import.meta.env.VITE_TOKEN_GRANT_TYPE)
      params.append('client_id', import.meta.env.VITE_TOKEN_CLIENT_ID)
      params.append('client_secret', import.meta.env.VITE_TOKEN_CLIENT_SECRET)
      params.append('scope', import.meta.env.VITE_TOKEN_SCOPE)

      const response = await fetch(`${import.meta.env.VITE_BASE_URL}/token?${params.toString()}`, {
        method: 'POST',
      })

      // fetch NO lanza ante 401/403/500: hay que validar el status explícitamente.
      if (!response.ok) {
        throw new Error(`Token request failed with status ${response.status}`)
      }

      const data = await response.json()

      // Sin access_token o sin expires_in válido no podemos programar la renovación
      // (el cálculo de delay daría NaN → setTimeout a 0ms → bucle infinito).
      if (!data || !data.access_token || !data.expires_in) {
        throw new Error('Token response did not contain a valid access_token/expires_in')
      }

      const token: string = data.access_token
      this.access_token = token
      this.tokenExpiry = data.expires_in // en segundos
      this.lastFetched = Date.now()
      this.retryDelay = timeoutRecall // reset del backoff tras éxito

      // Renovar 30s antes de expirar (mínimo 5s para no encadenar llamadas).
      const renewIn = Math.max((this.tokenExpiry - 30) * 1000, timeoutRecall)
      this.scheduleNextCall(renewIn)

      this.notifySubscribers()
      return token
    } catch (error) {
      console.error('Error fetching token:', error)

      // Backoff exponencial acotado: 5s, 10s, 20s, ... hasta maxRecall.
      // Evita el bombardeo de peticiones instantáneas cuando el /token rechaza.
      this.scheduleNextCall(this.retryDelay)
      this.retryDelay = Math.min(this.retryDelay * 2, maxRecall)

      // No rechazamos: devolvemos el último token conocido (o '') para no colgar
      // ni romper a los callers; el reintento programado repondrá el token.
      return this.access_token ?? ''
    }
  }

  private scheduleNextCall(delay: number) {
    if (this.updateCaller) {
      clearTimeout(this.updateCaller)
    }
    this.updateCaller = setTimeout(() => {
      this.getToken()
    }, delay)
  }

  isTokenValid(): boolean {
    if (!this.access_token || !this.lastFetched) return false
    const now = Date.now()
    return now - this.lastFetched < this.tokenExpiry * 1000
  }

  async updateToken() {
    if (this.isTokenValid()) {
      return this.access_token
    }
    await this.getToken()
    return this.access_token
  }

  subscribe(callback: (data: string | null) => void) {
    this.subscribers.add(callback)
  }

  unsubscribe(callback: (data: string | null) => void) {
    this.subscribers.delete(callback)
  }

  private notifySubscribers() {
    this.tokenRef.value = this.access_token
    this.subscribers.forEach((callback) => callback(this.access_token))
  }

  public getRefToken(): Ref<string | null> {
    this.tokenRef.value = this.access_token
    return this.tokenRef
  }

  public getCurrentToken(): string | null {
    return this.access_token
  }
}

const tokenManager = new TokenManager()
export default tokenManager
